import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useRealtime } from "@/lib/ws";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, ArrowRight, CheckCircle2, Truck, Package, XCircle } from "lucide-react";

const STATUS_COLOR = {
  DRAFT: "bg-slate-400", REQUESTED: "bg-amber-500", APPROVED: "bg-blue-500",
  IN_TRANSIT: "bg-indigo-500", RECEIVED: "bg-emerald-500", COMPLETED: "bg-emerald-600", CANCELLED: "bg-rose-500",
};

export default function TransfersPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [open, setOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(null);
  const [form, setForm] = useState({ source_type: "warehouse", source_id: "warehouse-pusat", dest_type: "outlet", dest_id: "", items: [], notes: "" });
  const [pickIng, setPickIng] = useState("");
  const [pickQty, setPickQty] = useState("");

  const canApprove = ["owner", "manager"].includes(user?.role);
  const canDispatch = ["owner", "manager", "gudang"].includes(user?.role);

  const load = () => api.get("/transfers").then(({data}) => setTransfers(data));

  useEffect(() => {
    api.get("/outlets").then(({data}) => { setOutlets(data); setForm(f=>({...f, dest_id: data[0]?.id || ""})); });
    api.get("/ingredients").then(({data}) => setIngredients(data));
    load();
  }, []);

  useRealtime((msg) => {
    if (msg.event?.startsWith("stock_transfer.")) load();
  });

  useEffect(() => {
    const payload = location.state?.inventoryTransferItems;
    if (!Array.isArray(payload) || payload.length === 0) return;

    const first = payload[0];
    const source_type = first.location_type;
    const source_id = first.location_id;

    const valid = ["warehouse", "outlet"].includes(source_type)
      && Boolean(source_id)
      && payload.every(item =>
        item.location_type === source_type
        && item.location_id === source_id
        && ["product", "ingredient"].includes(item.entity)
        && Boolean(item.entity_id)
        && Number.isFinite(Number(item.qty))
        && Number(item.qty) > 0
      );

    if (!valid) {
      toast.error("Draft Transfer tidak valid atau lokasi asal berbeda");
      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    setForm(f => ({
      ...f,
      source_type,
      source_id,
      items: payload.map(item => ({
        entity: item.entity,
        entity_id: item.entity_id,
        name: item.name || "",
        qty: Number(item.qty),
        unit: item.unit || "pcs"
      })),
      notes: f.notes || "Draft dari Inventory"
    }));
    setOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.key, location.pathname, location.state, navigate]);


  function addItem() {
    const ing = ingredients.find(i => i.id === pickIng);
    if (!ing || !pickQty) { toast.error("Pilih bahan & qty"); return; }
    setForm(f => ({ ...f, items: [...f.items, { entity: "ingredient", entity_id: ing.id, name: ing.name, qty: Number(pickQty), unit: ing.unit }] }));
    setPickIng(""); setPickQty("");
  }

  async function createTransfer() {
    if (!form.items.length) { toast.error("Tambahkan minimal 1 item"); return; }
    try {
      await api.post("/transfers", form);
      toast.success("Transfer dibuat, menunggu approval");
      setOpen(false);
      setForm({ source_type: "warehouse", source_id: "warehouse-pusat", dest_type: "outlet", dest_id: outlets[0]?.id || "", items: [], notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }

  async function action(tid, act) {
    try {
      await api.post(`/transfers/${tid}/${act}`, act === "receive" ? { items: [], notes: "" } : {});
      toast.success(`Transfer ${act}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }

  async function submitReceive(t) {
    const items = t.items.map(it => ({
      entity_id: it.entity_id,
      received_qty: Number(document.querySelector(`[data-recv="${it.entity_id}"]`)?.value || it.qty),
      variance_reason: document.querySelector(`[data-reason="${it.entity_id}"]`)?.value || "",
    }));
    try {
      await api.post(`/transfers/${t.id}/receive`, { items, notes: "" });
      toast.success("Transfer diterima");
      setReceiveOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }

  const outletName = (id) => id === "warehouse-pusat" ? "GUDANG PUSAT" : (outlets.find(o=>o.id===id)?.name || id);

  return (
    <div className="page-shell">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div data-workspace-redundant-header><div>
          <h1 className="text-2xl font-extrabold font-display">Stock Transfer</h1></div>
          <p className="text-slate-500 text-sm">Alur transfer Gudang ↔ Outlet dengan approval & partial receiving</p>
        </div>
        <Button data-testid="transfer-add-btn" onClick={()=>setOpen(true)} className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Transfer Baru</Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Daftar Transfer · {transfers.length}</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>No.</TableHead><TableHead>Rute</TableHead><TableHead>Item</TableHead>
              <TableHead>Dibuat</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {transfers.map(t => (
                <TableRow key={t.id}>
                  <TableCell className="font-mono text-xs">{t.transfer_no}</TableCell>
                  <TableCell className="text-sm">
                    <span className="text-slate-600">{outletName(t.source_id)}</span>
                    <ArrowRight className="inline w-3 h-3 mx-1 text-slate-400"/>
                    <span className="font-medium">{outletName(t.dest_id)}</span>
                  </TableCell>
                  <TableCell className="text-xs">{t.items.length} bahan</TableCell>
                  <TableCell className="text-xs">{new Date(t.created_at).toLocaleString("id-ID")}</TableCell>
                  <TableCell><Badge className={STATUS_COLOR[t.status]}>{t.status}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    {t.status === "REQUESTED" && canApprove && (
                      <Button data-testid={`transfer-approve-${t.id}`} size="sm" onClick={()=>action(t.id, "approve")} className="bg-blue-500 hover:bg-blue-600"><CheckCircle2 className="w-3 h-3 mr-1"/>Approve</Button>
                    )}
                    {t.status === "APPROVED" && canDispatch && (
                      <Button data-testid={`transfer-dispatch-${t.id}`} size="sm" onClick={()=>action(t.id, "dispatch")} className="bg-indigo-500 hover:bg-indigo-600"><Truck className="w-3 h-3 mr-1"/>Dispatch</Button>
                    )}
                    {t.status === "IN_TRANSIT" && (
                      <Button data-testid={`transfer-receive-${t.id}`} size="sm" onClick={()=>setReceiveOpen(t)} className="bg-emerald-500 hover:bg-emerald-600"><Package className="w-3 h-3 mr-1"/>Terima</Button>
                    )}
                    {["REQUESTED", "APPROVED"].includes(t.status) && canApprove && (
                      <Button size="sm" variant="outline" onClick={()=>action(t.id, "cancel")}><XCircle className="w-3 h-3"/></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!transfers.length && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Belum ada transfer</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Transfer Baru</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sumber</Label>
              <Select value={form.source_type + ":" + form.source_id} onValueChange={v=>{const [t,i] = v.split(":"); setForm({...form, source_type: t, source_id: i});}}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="warehouse:warehouse-pusat">GUDANG PUSAT</SelectItem>
                  {outlets.map(o=><SelectItem key={o.id} value={`outlet:${o.id}`}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tujuan</Label>
              <Select value={form.dest_type + ":" + form.dest_id} onValueChange={v=>{const [t,i] = v.split(":"); setForm({...form, dest_type: t, dest_id: i});}}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="warehouse:warehouse-pusat">GUDANG PUSAT</SelectItem>
                  {outlets.map(o=><SelectItem key={o.id} value={`outlet:${o.id}`}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">Bahan</Label>
                <Select value={pickIng} onValueChange={setPickIng}>
                  <SelectTrigger data-testid="transfer-pick-ingredient"><SelectValue placeholder="Pilih bahan..."/></SelectTrigger>
                  <SelectContent>{ingredients.map(i=><SelectItem key={i.id} value={i.id}>{i.name} (stok {i.stock} {i.unit})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="w-28">
                <Label className="text-xs">Qty</Label>
                <Input data-testid="transfer-pick-qty" type="number" value={pickQty} onChange={e=>setPickQty(e.target.value)}/>
              </div>
              <Button data-testid="transfer-add-item" onClick={addItem} size="sm">Tambah</Button>
            </div>
            {form.items.length > 0 && (
              <div className="text-xs space-y-1 pt-2 border-t">
                {form.items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <span>{it.name} × {it.qty} {it.unit}</span>
                    <button className="text-rose-500" onClick={()=>setForm({...form, items: form.items.filter((_,j)=>j!==i)})}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div><Label>Catatan</Label><Input value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})}/></div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button data-testid="transfer-submit" onClick={createTransfer} className="bg-orange-500 hover:bg-orange-600">Buat Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={!!receiveOpen} onOpenChange={(o)=>!o && setReceiveOpen(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Terima Transfer {receiveOpen?.transfer_no}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-2">Isi qty aktual yang diterima. Selisih akan tercatat sebagai variance.</div>
          <div className="space-y-2 max-h-80 overflow-auto">
            {receiveOpen?.items.map(it => (
              <div key={it.entity_id} className="grid grid-cols-3 gap-2 p-2 bg-slate-50 rounded">
                <div className="text-sm">
                  <div className="font-medium">{it.name}</div>
                  <div className="text-xs text-slate-500">Sent: {it.qty} {it.unit}</div>
                </div>
                <Input data-recv={it.entity_id} type="number" step="0.01" defaultValue={it.qty} placeholder="Received qty"/>
                <Input data-reason={it.entity_id} placeholder="Alasan variance (jika ada)"/>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setReceiveOpen(null)}>Batal</Button>
            <Button data-testid="transfer-receive-submit" onClick={()=>submitReceive(receiveOpen)} className="bg-emerald-500 hover:bg-emerald-600">Konfirmasi Terima</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
